import { Root } from "protobufjs";

import type {
  EntitiesInGroup,
  EntitiesInGroupModel
} from "components/map/contexts/MapContextState";

export interface EntitiesOnMapResult {
  groupsMap: { [group: string]: EntitiesInGroup };
}

const entitiesOnMapRoot = Root.fromJSON({
  nested: {
    EntitiesOnMapResult: {
      fields: {
        groupsMap: {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          keyType: "string",
          type: ".EntitiesInGroup",
          id: 1
        }
      }
    },
    EntityGroup: {
      fields: {
        id: {
          type: "int32",
          id: 1
        },
        title: {
          type: "string",
          id: 2
        },
        color: {
          type: "string",
          id: 3
        }
      }
    },
    EntityDetail: {
      fields: {
        uwi: {
          type: "string",
          id: 1
        },
        groupId: {
          type: "int32",
          id: 2
        }
      }
    },
    EntitiesInGroup: {
      fields: {
        uwis: {
          rule: "repeated",
          type: "string",
          id: 1
        },
        group: {
          type: "EntityGroup",
          id: 2
        }
      }
    }
  }
});

const entitiesOnMapType = entitiesOnMapRoot.lookupType("EntitiesOnMapResult");

export function toEntitiesOnMapArrayBuffer(
  payload: ArrayBuffer | Uint8Array
): ArrayBuffer {
  if (payload instanceof ArrayBuffer) {
    return payload;
  }

  return payload.buffer.slice(
    payload.byteOffset,
    payload.byteOffset + payload.byteLength
  ) as ArrayBuffer;
}

export function decodeEntitiesOnMapPayload(
  payload: ArrayBuffer | Uint8Array
): Record<string, EntitiesInGroupModel> {
  const data = payload instanceof Uint8Array ? payload : new Uint8Array(payload);
  const message = entitiesOnMapType.decode(data);
  // arrays: true so a group with no wells decodes `uwis` to [] instead of
  // omitting it — otherwise `group.uwis` is undefined and the for..of below
  // throws, aborting the whole decode.
  const obj = entitiesOnMapType.toObject(message, {
    arrays: true
  }) as EntitiesOnMapResult;

  if (!obj?.groupsMap) {
    return {};
  }

  const result: Record<string, EntitiesInGroupModel> = {};

  for (const [key, group] of Object.entries(obj.groupsMap)) {
    const uwis: Record<string, string> = {};

    for (const uwi of group.uwis) {
      // Fall back to the "not in group" color (#212121) rather than "" — an
      // empty string is not a valid color and parses to NaN downstream
      // (ThreeDViewer parseInt) / breaks Mapbox paint expressions.
      uwis[uwi] = group.group?.color ?? "#212121";
    }

    result[key] = {
      uwis,
      group: group.group
    };
  }

  return result;
}
